import axios from "axios";
import { downscaleImageForAiEdit } from "../helpers/product-image-upload";

const rawBase = process.env.REACT_APP_API_URL || "";
const apiTargetUrl = rawBase.replace(/\/$/, "");
const useDevProxy = process.env.NODE_ENV === "development" && Boolean(apiTargetUrl);
const baseURL = useDevProxy ? "" : apiTargetUrl;
const AUTH_TOKEN_KEY = "aa_shop_auth_token";
const AUTH_USER_KEY = "aa_shop_auth_user";
const ALLOWED_LOGIN_ROLES = [
  "user",
  "reseller_admin",
  "super_admin",
  "admin",
  "manager",
  "sales",
  "financial_cash",
  "financial_bank",
  "admin_price_change",
  "image_upload"
];

export const api = axios.create({
  baseURL: baseURL || undefined,
  headers: {
    Accept: "application/json",
  },
  timeout: 15000,
});

export function getApiBaseUrl() {
  return apiTargetUrl;
}

export function fetchApiTest() {
  return api.get("/api/test");
}

/** Public reference data: country, states[], cities_by_state{} */
export function fetchMexicoAddresses() {
  return api.get("/api/meta/mexico-addresses");
}

export function fetchAccountAddresses() {
  return api.get("/api/account/addresses");
}

export function createAccountAddress(payload) {
  return api.post("/api/account/addresses", payload);
}

export function fetchResellerMyProducts() {
  return api.get("/api/reseller/shop/my-products");
}

export function getStoredToken() {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function setStoredToken(token) {
  if (!token) {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    return;
  }
  localStorage.setItem(AUTH_TOKEN_KEY, token);
}

export function getStoredUser() {
  const raw = localStorage.getItem(AUTH_USER_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch (error) {
    localStorage.removeItem(AUTH_USER_KEY);
    return null;
  }
}

export function setStoredUser(user) {
  if (!user) {
    localStorage.removeItem(AUTH_USER_KEY);
    return;
  }
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
}

export function clearAuthStorage() {
  setStoredToken(null);
  setStoredUser(null);
}

function createApiError(message, responseData = {}) {
  const error = new Error(message);
  error.response = {
    data: {
      message,
      ...responseData
    }
  };
  return error;
}

function normalizeAuthResponse(data) {
  if (!data?.success || !data?.token || !data?.user) {
    throw createApiError(data?.message || "Login failed.");
  }

  if (data.user.is_active === false) {
    throw createApiError("Your account is inactive.");
  }

  if (!ALLOWED_LOGIN_ROLES.includes(data.user.role)) {
    throw createApiError("Your account role is not allowed to log in here.");
  }

  return {
    ...data,
    user: {
      ...data.user,
      role: data.user.role
    }
  };
}

api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export async function login(payload) {
  const response = await api.post("/api/auth/login", payload);
  return normalizeAuthResponse(response.data);
}

export async function checkReferral(payload) {
  const response = await api.post("/api/auth/check-referral", payload);
  return response.data;
}

export async function register(payload) {
  const response = await api.post("/api/auth/register", payload);
  return normalizeAuthResponse(response.data);
}

/** Reseller admin: customer signup invite link (`/register?ref=...` — shop UI built separately). */
export async function getMyReferralLink() {
  const base =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : undefined;
  const response = await api.get("/api/reseller/me/referral-link", {
    params: base ? { base } : undefined
  });
  return response.data;
}

export async function fetchResellerProductVariants(resellerProductId) {
  const response = await api.get(`/api/reseller/products/${resellerProductId}/variants`);
  return response.data;
}

export async function updateResellerProductVariants(resellerProductId, payload) {
  const response = await api.put(`/api/reseller/products/${resellerProductId}/variants`, payload);
  return response.data;
}

export async function logout() {
  const response = await api.post("/api/auth/logout");
  return response.data;
}

export async function getCurrentUser() {
  const response = await api.get("/api/auth/me");
  return response.data;
}

export async function fetchShopCategories() {
  const response = await api.get("/api/shop/categories");
  return response.data;
}

export async function fetchShopProducts() {
  const response = await api.get("/api/shop/products");
  return response.data;
}

export async function fetchCustomerProfile() {
  const response = await api.get("/api/customer/profile");
  return response.data;
}

export async function fetchCustomerAddress() {
  const response = await api.get("/api/customer/profile/address");
  return response.data;
}

export async function fetchCustomerOrders() {
  const response = await api.get("/api/customer/orders");
  return response.data;
}

export async function fetchCustomerOrder(orderId) {
  const response = await api.get(`/api/customer/orders/${orderId}`);
  return response.data;
}

export async function fetchCustomerProducts() {
  const response = await api.get("/api/customer/shop/products");
  return response.data;
}

export async function fetchResellerShopProducts() {
  const response = await api.get("/api/reseller/shop/products");
  return response.data;
}

function getShopRolePrefix(role) {
  if (role === "reseller_admin") return "/api/reseller/shop";
  if (role === "user") return "/api/customer/shop";

  throw createApiError("You must be logged in to use the cart.");
}

export async function fetchShopCart(role) {
  const response = await api.get(`${getShopRolePrefix(role)}/cart`);
  return response.data;
}

export async function addShopCartItem(role, payload) {
  const response = await api.post(`${getShopRolePrefix(role)}/cart/items`, payload);
  return response.data;
}

export async function updateShopCartItem(role, cartItemId, payload) {
  const response = await api.patch(`${getShopRolePrefix(role)}/cart/items/${cartItemId}`, payload);
  return response.data;
}

export async function removeShopCartItem(role, cartItemId) {
  const response = await api.delete(`${getShopRolePrefix(role)}/cart/items/${cartItemId}`);
  return response.data;
}

export async function clearShopCart(role) {
  const response = await api.delete(`${getShopRolePrefix(role)}/cart`);
  return response.data;
}

export async function fetchShopCheckoutPreview(role) {
  const response = await api.post(`${getShopRolePrefix(role)}/checkout/preview`);
  return response.data;
}

export async function placeShopOrder(role, payload = {}) {
  const response = await api.post(`${getShopRolePrefix(role)}/checkout/place-order`, payload);
  return response.data;
}

export async function placeCustomerOrder(payload) {
  const response = await api.post("/api/customer/orders", payload);
  return response.data;
}

export async function updateCustomerProfile(payload) {
  const response = await api.patch("/api/customer/profile", payload);
  return response.data;
}

export async function updateCustomerAddress(payload) {
  const response = await api.patch("/api/customer/profile/address", payload);
  return response.data;
}

export async function deleteCustomerProfile() {
  const response = await api.delete("/api/customer/profile");
  return response.data;
}

export async function fetchAdminDashboard() {
  const response = await api.get("/api/admin/dashboard");
  return response.data;
}

export async function fetchAdminUsers(params = {}) {
  const response = await api.get("/api/admin/users", { params });
  return response.data;
}

export async function fetchAdminResellerUsers(params = {}) {
  const response = await api.get("/api/admin/users", { params: { ...params, role: "reseller_admin" } });
  return response.data;
}

export async function fetchAdminUser(userId) {
  const response = await api.get(`/api/admin/users/${userId}`);
  return response.data;
}

export async function createAdminUser(payload) {
  const response = await api.post("/api/admin/users", payload);
  return response.data;
}

export async function updateAdminUser(userId, payload) {
  const response = await api.patch(`/api/admin/users/${userId}`, payload);
  return response.data;
}

export async function setAdminUserStatus(userId, isActive) {
  const response = await api.patch(`/api/admin/users/${userId}/status`, { is_active: isActive });
  return response.data;
}

export async function deleteAdminUser(userId) {
  const response = await api.delete(`/api/admin/users/${userId}`);
  return response.data;
}

export async function updateAdminUserRole(userId, payload) {
  const response = await api.patch(`/api/admin/users/${userId}/role`, payload);
  return response.data;
}

export async function fetchAdminRoles() {
  const response = await api.get("/api/admin/roles");
  return response.data;
}

export async function updateAdminRole(roleId, payload) {
  const response = await api.patch(`/api/admin/roles/${roleId}`, payload);
  return response.data;
}

export async function fetchAdminOrders(params = {}) {
  const response = await api.get("/api/admin/orders", { params });
  return response.data;
}

export async function fetchAdminOrder(orderId) {
  const response = await api.get(`/api/admin/orders/${orderId}`);
  return response.data;
}

export async function updateAdminOrder(orderId, payload) {
  if (payload instanceof FormData) {
    payload.append("_method", "PATCH");
    const response = await api.post(`/api/admin/orders/${orderId}`, payload);
    return response.data;
  }
  const response = await api.patch(`/api/admin/orders/${orderId}`, payload);
  return response.data;
}

export async function runAdminOrderApprovalAction(orderId, action, note = "") {
  const payload = { approval_action: action };
  if (note) {
    payload.payment_approval_note = note;
  }
  const response = await api.patch(`/api/admin/orders/${orderId}`, payload);
  return response.data;
}

// Carrier calls (Enviatodo) are slow; allow a longer timeout than the 15s default.
const SHIPPING_REQUEST_TIMEOUT_MS = 60000;

export async function getOrderShippingRates(orderId) {
  const response = await api.post(
    `/api/admin/orders/${orderId}/shipping/quote`,
    {},
    { timeout: SHIPPING_REQUEST_TIMEOUT_MS }
  );
  return response.data;
}

export async function generateOrderShippingLabel(orderId, body) {
  const response = await api.post(
    `/api/admin/orders/${orderId}/shipping/label`,
    body,
    { timeout: SHIPPING_REQUEST_TIMEOUT_MS }
  );
  return response.data;
}

export async function getOrderShippingLabel(orderId) {
  const response = await api.get(`/api/admin/orders/${orderId}/shipping/label`, {
    timeout: SHIPPING_REQUEST_TIMEOUT_MS
  });
  return response.data;
}

export async function trackOrderShipment(orderId) {
  const response = await api.get(`/api/admin/orders/${orderId}/shipping/track`);
  return response.data;
}

export async function fetchAdminProducts(params = {}) {
  const response = await api.get("/api/admin/products", { params });
  return response.data;
}

export async function fetchAdminProductPriceChangeLogs(params = {}) {
  const response = await api.get("/api/admin/product-price-change-logs", { params });
  return response.data;
}

const MULTIPART_UPLOAD_TIMEOUT_MS = 300000;

export async function createAdminProduct(payload) {
  const config = payload instanceof FormData ? { timeout: MULTIPART_UPLOAD_TIMEOUT_MS } : undefined;
  const response = await api.post("/api/admin/products", payload, config);
  return response.data;
}

export async function fetchAdminProduct(productId) {
  const response = await api.get(`/api/admin/products/${productId}`);
  return response.data;
}

export async function updateAdminProduct(productId, payload) {
  if (payload instanceof FormData) {
    payload.append("_method", "PATCH");
    const response = await api.post(`/api/admin/products/${productId}`, payload, {
      timeout: MULTIPART_UPLOAD_TIMEOUT_MS,
    });
    return response.data;
  }
  const response = await api.patch(`/api/admin/products/${productId}`, payload);
  return response.data;
}

// AI image generation regularly takes 1-2 minutes.
const AI_IMAGE_EDIT_TIMEOUT_MS = 300000;

export async function fetchAdminAiImageEditConfig() {
  const response = await api.get("/api/admin/ai-image-edit/config");
  return response.data;
}

export async function generateAdminProductImageAiPreview(productId, imageId, payload = {}) {
  const response = await api.post(
    `/api/admin/products/${productId}/gallery-images/${imageId}/ai-edit/preview`,
    payload,
    { timeout: AI_IMAGE_EDIT_TIMEOUT_MS }
  );
  return response.data;
}

export async function applyAdminProductImageAiEdit(productId, imageId, payload) {
  const response = await api.post(
    `/api/admin/products/${productId}/gallery-images/${imageId}/ai-edit/apply`,
    payload
  );
  return response.data;
}

export async function generateAdminProductThumbnailAiPreview(productId, payload = {}) {
  const response = await api.post(
    `/api/admin/products/${productId}/thumbnail/ai-edit/preview`,
    payload,
    { timeout: AI_IMAGE_EDIT_TIMEOUT_MS }
  );
  return response.data;
}

export async function applyAdminProductThumbnailAiEdit(productId, payload) {
  const response = await api.post(
    `/api/admin/products/${productId}/thumbnail/ai-edit/apply`,
    payload
  );
  return response.data;
}

export async function generateAdminAiImagePreviewFromFile(file, prompt) {
  const upload = await downscaleImageForAiEdit(file);
  const payload = new FormData();
  payload.append("image", upload, upload.name);
  if (prompt) payload.append("prompt", prompt);
  const response = await api.post("/api/admin/ai-image-edit/preview-upload", payload, {
    timeout: AI_IMAGE_EDIT_TIMEOUT_MS,
  });
  return response.data;
}

export async function downloadAdminAiImagePreview(previewPath) {
  const response = await api.get("/api/admin/ai-image-edit/preview-file", {
    params: { preview_path: previewPath },
    responseType: "blob",
  });
  return response.data;
}

export async function discardAdminAiImagePreview(previewPath) {
  const response = await api.post("/api/admin/ai-image-edit/discard", { preview_path: previewPath });
  return response.data;
}

export async function fetchAdminProductCategories(params = {}) {
  const response = await api.get("/api/admin/product-categories", { params });
  return response.data;
}

export async function fetchAdminProductCategory(categoryId) {
  const response = await api.get(`/api/admin/product-categories/${categoryId}`);
  return response.data;
}

export async function createAdminProductCategory(payload) {
  const response = await api.post("/api/admin/product-categories", payload);
  return response.data;
}

export async function updateAdminProductCategory(categoryId, payload) {
  const response = await api.patch(`/api/admin/product-categories/${categoryId}`, payload);
  return response.data;
}

export async function deleteAdminProductCategory(categoryId) {
  const response = await api.delete(`/api/admin/product-categories/${categoryId}`);
  return response.data;
}

export async function fetchAdminMexicoStates() {
  const response = await api.get("/api/admin/mexico-locations/states");
  return response.data;
}

export async function fetchAdminMexicoCities(stateId, params = {}) {
  const response = await api.get(`/api/admin/mexico-locations/states/${stateId}/cities`, { params });
  return response.data;
}

export async function createAdminMexicoCity(payload) {
  const response = await api.post("/api/admin/mexico-locations/cities", payload);
  return response.data;
}

export async function updateAdminMexicoCity(cityId, payload) {
  const response = await api.patch(`/api/admin/mexico-locations/cities/${cityId}`, payload);
  return response.data;
}

export async function deleteAdminMexicoCity(cityId) {
  const response = await api.delete(`/api/admin/mexico-locations/cities/${cityId}`);
  return response.data;
}

export async function fetchAdminBankDatas(params = {}) {
  const response = await api.get("/api/admin/bank-datas", { params });
  return response.data;
}

export async function createAdminBankData(payload) {
  const response = await api.post("/api/admin/bank-datas", payload);
  return response.data;
}

export async function updateAdminBankData(bankDataId, payload) {
  const response = await api.patch(`/api/admin/bank-datas/${bankDataId}`, payload);
  return response.data;
}

export async function setAdminBankDataStatus(bankDataId, isActive) {
  const response = await api.patch(`/api/admin/bank-datas/${bankDataId}/status`, { is_active: isActive });
  return response.data;
}

export async function deleteAdminBankData(bankDataId) {
  const response = await api.delete(`/api/admin/bank-datas/${bankDataId}`);
  return response.data;
}

export async function fetchAdminInventory(params = {}) {
  const response = await api.get("/api/admin/inventory", { params });
  return response.data;
}

export async function fetchAdminInventoryResellers(params = {}) {
  const response = await api.get("/api/admin/inventory/resellers", { params });
  return response.data;
}

export async function createInventoryMovement(payload) {
  const response = await api.post("/api/admin/inventory/movements", payload);
  return response.data;
}

export async function fetchAdminNotifications() {
  const response = await api.get("/api/admin/notifications");
  return response.data;
}

export async function fetchResellerPurchaseOrders(params = {}) {
  const response = await api.get("/api/admin/reseller-purchase-orders", { params });
  return response.data;
}

export async function fetchResellerPurchaseOrder(orderId) {
  const response = await api.get(`/api/admin/reseller-purchase-orders/${orderId}`);
  return response.data;
}

export async function fetchResellerPurchaseOrderInvoice(orderId) {
  const response = await api.get(`/api/admin/reseller-purchase-orders/${orderId}/invoice`);
  return response.data;
}

export async function updateResellerPurchaseOrder(orderId, payload) {
  if (payload instanceof FormData) {
    payload.append("_method", "PATCH");
    const response = await api.post(`/api/admin/reseller-purchase-orders/${orderId}`, payload);
    return response.data;
  }
  const response = await api.patch(`/api/admin/reseller-purchase-orders/${orderId}`, payload);
  return response.data;
}

export async function fetchResellerDebits(userId, params = {}) {
  const response = await api.get(`/api/admin/resellers/${userId}/debits`, { params });
  return response.data;
}

export async function grantResellerDebit(userId, payload) {
  const response = await api.post(`/api/admin/resellers/${userId}/debits/grant`, payload);
  return response.data;
}

export async function adjustResellerDebit(userId, payload) {
  const response = await api.post(`/api/admin/resellers/${userId}/debits/adjust`, payload);
  return response.data;
}

export async function fetchResellerPurchaseOrderDebitLog(orderId) {
  const response = await api.get(`/api/admin/reseller-purchase-orders/${orderId}/debit-log`);
  return response.data;
}

export async function createResellerPurchaseOrder(payload) {
  const response = await api.post("/api/admin/reseller-purchase-orders", payload);
  return response.data;
}

export async function approveResellerPurchaseOrder(orderId) {
  const response = await api.patch(`/api/admin/reseller-purchase-orders/${orderId}/approve`);
  return response.data;
}

export async function rejectResellerPurchaseOrder(orderId) {
  const response = await api.patch(`/api/admin/reseller-purchase-orders/${orderId}/reject`);
  return response.data;
}

export async function fulfillResellerPurchaseOrder(orderId) {
  const response = await api.patch(`/api/admin/reseller-purchase-orders/${orderId}/fulfill`);
  return response.data;
}

export async function submitResellerPurchaseOrderPayment(orderId, payload) {
  const formData = payload instanceof FormData ? payload : new FormData();
  if (!(payload instanceof FormData)) {
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });
  }
  const response = await api.post(`/api/admin/reseller-purchase-orders/${orderId}/payments`, formData);
  return response.data;
}

export async function reviewResellerPurchaseOrderPayment(orderId, paymentId, payload) {
  const response = await api.patch(
    `/api/admin/reseller-purchase-orders/${orderId}/payments/${paymentId}`,
    payload
  );
  return response.data;
}

export async function deleteResellerPurchaseOrderPayment(orderId, paymentId) {
  const response = await api.delete(`/api/admin/reseller-purchase-orders/${orderId}/payments/${paymentId}`);
  return response.data;
}

export async function downloadAdminReport(type = "orders", params = {}) {
  const response = await api.get("/api/admin/reports/export", {
    params: { type, ...params },
    responseType: "blob"
  });
  return response;
}

export async function downloadAdminPriceListPdf(params = {}) {
  const response = await api.get("/api/admin/products/price-list/export.pdf", {
    params,
    responseType: "blob"
  });
  return response;
}

/**
 * Sends a SHA-256 hex hash to the server and returns duplicate-detection info.
 * Response shape: { duplicate: boolean, existing_file?: { original_filename, context_type, context_id, uploaded_at } }
 */
export function checkFileDuplicate(hash) {
  return api.post("/api/admin/files/check-duplicate", { hash });
}

export async function deleteDocument(documentId) {
  const response = await api.delete(`/api/admin/documents/${documentId}`);
  return response.data;
}

export function fetchResellerStorePaymentSettings() {
  return api.get("/api/reseller/store-settings/payment");
}

export function updateResellerStorePaymentSettings(payload) {
  return api.patch("/api/reseller/store-settings/payment", payload);
}

export async function fetchCustomerPaymentInfo() {
  const response = await api.get("/api/customer/shop/payment-info");
  return response.data;
}
